// Порт TDLib match_mentions / match_hashtags (td/telegram/MessageEntity.cpp).
// Перенесён из docs/prototypes/telegram-preview/src/detect.ts без изменения алгоритма.
// TDLib iterates UTF-8 bytes; we iterate UTF-16 code units but decode code points,
// so returned offsets are already UTF-16 units = Bot API MessageEntity.offset.

const RE_LETTER = /\p{L}/u;
const RE_ND = /\p{Nd}/u;
const RE_N = /\p{N}/u;
const RE_SEP = /\p{Z}/u;

type Cat = 'Letter' | 'DecimalNumber' | 'Number' | 'Separator' | 'Unknown';
export function category(ch: string): Cat {
  if (RE_LETTER.test(ch)) return 'Letter';
  if (RE_ND.test(ch)) return 'DecimalNumber';
  if (RE_N.test(ch)) return 'Number';
  if (RE_SEP.test(ch)) return 'Separator';
  return 'Unknown';
}
/** Индексация строки под noUncheckedIndexedAccess. Алгоритм ниже всегда индексирует
 *  в границах — проверено 227 золотыми векторами TDLib; помощник снимает `| undefined`,
 *  не меняя поведения. */
const cu = (s: string, i: number): string => s[i] as string;
const isWordChar = (ch: string) => {
  const c = category(ch);
  return c === 'Letter' || c === 'DecimalNumber' || c === 'Number' || ch === '_';
};
const isAlphaDigitOrUnderscore = (ch: string) => /^[0-9a-zA-Z_]$/.test(ch);
const isHashtagLetter = (ch: string) => {
  const cp = ch.codePointAt(0) as number;
  if (ch === '_' || cp === 0x200c || cp === 0xb7 || (cp >= 0xd80 && cp <= 0xdff)) return true;
  const c = category(ch);
  return c === 'Letter' || c === 'DecimalNumber';
};
const VALID_SHORT_USERNAMES = new Set(['gif', 'nft', 'pic', 'ufc', 'vid']);

/** code point starting at UTF-16 index i (or the one ending at i, for prev) */
const cpAt = (s: string, i: number) => String.fromCodePoint(s.codePointAt(i) as number);
const prevCp = (s: string, i: number) => {
  const c = s.charCodeAt(i - 1);
  if (c >= 0xdc00 && c <= 0xdfff && i >= 2) return s.slice(i - 2, i);
  return cu(s, i - 1);
};

export type Match = { offset: number; length: number };

export function matchMentions(s: string): Match[] {
  const out: Match[] = [];
  let ptr = 0;
  while (true) {
    const at = s.indexOf('@', ptr);
    if (at < 0) break;
    if (at !== 0 && isWordChar(prevCp(s, at))) {
      ptr = at + 1;
      continue;
    }
    let p = at + 1;
    while (p < s.length && isAlphaDigitOrUnderscore(cu(s, p))) p++;
    const size = p - (at + 1);
    ptr = p;
    if (size < 2 || size > 32) continue;
    if (p < s.length && isWordChar(cpAt(s, p))) continue;
    out.push({ offset: at, length: p - at });
  }
  return out;
}

export function findMentions(s: string): Match[] {
  return matchMentions(s).filter((m) => {
    const name = s.slice(m.offset + 1, m.offset + m.length);
    return name.length >= 4 || VALID_SHORT_USERNAMES.has(name.toLowerCase());
  });
}

export function matchHashtags(s: string): Match[] {
  const out: Match[] = [];
  let ptr = 0;
  while (true) {
    const at = s.indexOf('#', ptr);
    if (at < 0) break;
    if (at !== 0 && isHashtagLetter(prevCp(s, at))) {
      ptr = at + 1;
      continue;
    }
    let p = at + 1;
    let size = 0;
    let end = -1;
    let wasLetter = false;
    while (p < s.length) {
      const ch = cpAt(s, p);
      if (!isHashtagLetter(ch)) break;
      p += ch.length;
      if (size === 255) end = p;
      if (size !== 256) {
        if (category(ch) === 'Letter') wasLetter = true;
        size++;
      }
    }
    if (end < 0) end = p;
    ptr = p;
    if (size < 1) continue;
    if (end === p && p < s.length && cu(s, p) === '@') {
      let ue = p + 1;
      while (ue < s.length && ue - p < 33 && isAlphaDigitOrUnderscore(cu(s, ue))) ue++;
      if (ue - p - 1 >= 3) {
        p = ue;
        end = ue;
        ptr = p;
      }
    }
    if (p < s.length && cu(s, p) === '#') continue;
    if (!wasLetter) continue;
    out.push({ offset: at, length: end - at });
  }
  return out;
}
